// RelatorioTabular.js - Relatório em estilo planilha (Interface Web)
import React, { useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import {
  Box, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Select, MenuItem, FormControl,
  InputLabel, Button, Grid, Chip, Autocomplete, Alert, CircularProgress,
  TablePagination
} from '@mui/material';
import {
  Refresh, FilterList,
  Print
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const escapeHtml = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));

// Filiais de estoque relevantes para o filtro (apenas estas aparecem no dropdown).
// 14 = PRIN.SEDE, 27 = FOTO BANGU, 50 = PETROPOLIS MATRIZ — as únicas com volume
// real de estoque; as demais têm produção residual. Ajuste a lista para liberar
// outras filiais. Comparação por string (o código vem como texto da API).
const FILIAIS_ESTOQUE_PERMITIDAS = ['14', '27', '50'];

const RelatorioTabular = () => {
  const { api } = useAuth();
  
  // Estados principais
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dadosProcessados, setDadosProcessados] = useState([]);
  const [totaisDiarios, setTotaisDiarios] = useState({});
  const [totaisDiariosLeituras, setTotaisDiariosLeituras] = useState({});
  // Distinct global REAL (vem do backend; não é soma das células)
  const [totalDistinctReal, setTotalDistinctReal] = useState(0);
  const [metrica, setMetrica] = useState('distinct'); // 'distinct' | 'leituras'
  
  // Estados de filtros
  const [filtros, setFiltros] = useState({
    dataInicio: '',
    dataFim: '',
    horaInicio: '00:00',
    horaFim: '23:59',
    setor: '',
    funcionario: '',
    filialEstoque: ''
  });
  const [opcoesFiltro, setOpcoesFiltro] = useState({
    setores: [],
    funcionarios: [],
    filiais: []
  });
  const [funcionarioSelecionado, setFuncionarioSelecionado] = useState(null);
  
  // Estados de visualização
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [diasDoMes, setDiasDoMes] = useState([]);
  const [resultadoValidacao, setResultadoValidacao] = useState(null);
  const [validando, setValidando] = useState(false);
  const [imprimindo, setImprimindo] = useState(false);

  // Carregar opções de filtro
  const carregarOpcoesFiltro = useCallback(async () => {
    try {
      const [resSetores, resFuncionarios, resFiliais] = await Promise.all([
        api.listarSetores(),
        api.listarFuncionarios(),
        api.listarFiliais()
      ]);

      setOpcoesFiltro({
        setores: resSetores.success ? resSetores.dados : [],
        funcionarios: resFuncionarios.success ? resFuncionarios.dados : [],
        filiais: resFiliais.success ? resFiliais.dados : []
      });
    } catch (error) {
      console.error('❌ Erro ao carregar opções de filtro:', error);
    }
  }, [api]);

  // Normalizar chave de data para YYYY-MM-DD (API pode retornar ISO ou Date)
  const normalizarChaveData = (val) => {
    if (val == null) return '';
    const s = String(val).trim();
    const m = s.match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
    const ddmmyyyy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (ddmmyyyy) return `${ddmmyyyy[3]}-${ddmmyyyy[2].padStart(2, '0')}-${ddmmyyyy[1].padStart(2, '0')}`;
    return s;
  };

  // Gerar dias do período (timezone-safe) sempre em ordem crescente
  const gerarDiasDoPeriodo = (dataInicio, dataFim) => {
    if (!dataInicio || !dataFim) return [];
    const dias = [];
    const inicio = new Date(`${dataInicio}T00:00:00`);
    const fim = new Date(`${dataFim}T23:59:59`);
    const cursor = new Date(inicio.getTime());
    const nomesSemana = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
    while (cursor <= fim) {
      const ano = cursor.getFullYear();
      const mes = String(cursor.getMonth() + 1).padStart(2, '0');
      const diaNum = String(cursor.getDate()).padStart(2, '0');
      const dataStr = `${ano}-${mes}-${diaNum}`;
      dias.push({
        data: dataStr,
        dia: cursor.getDate(),
        diaSemana: nomesSemana[cursor.getDay()]
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return dias;
  };

  // Carregar dados do relatório
  const carregarDados = async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período (data início e fim)');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // Limpar dados anteriores antes de carregar novos
      setDadosProcessados([]);
      setTotaisDiarios({});
      setTotaisDiariosLeituras({});
      
      // Sempre enviar datas em YYYY-MM-DD para a API (evita DD/MM/YYYY do locale)
      const dataInicioNorm = normalizarChaveData(filtros.dataInicio) || filtros.dataInicio;
      const dataFimNorm = normalizarChaveData(filtros.dataFim) || filtros.dataFim;

      const parametros = {
        dataInicio: `${dataInicioNorm} ${filtros.horaInicio}:00`,
        dataFim: `${dataFimNorm} ${filtros.horaFim}:59`
      };

      if (filtros.setor) parametros.setor = filtros.setor;
      if (funcionarioSelecionado) parametros.funcionario = funcionarioSelecionado.codigo;
      if (filtros.filialEstoque) parametros.filialEstoque = filtros.filialEstoque;

      const res = await api.relatorioTabular(parametros);
      
      if (res.success) {
  const dadosTabular = res.dados;

        // Extrair estruturas vindas do backend.
        // Backend adiciona uma linha sintética "TOTAL" quando filtra por funcionário
        // com múltiplos setores — descartamos aqui pra evitar double-count nos somatórios.
  const funcionariosApi = (dadosTabular.funcionarios || []).filter(
    f => f.codigo !== 'TOTAL' && f.setor !== 'SOMA TOTAL'
  );

        // Gerar dias confiáveis a partir do filtro (evita dia 31 do mês anterior, etc.)
        const diasGerados = gerarDiasDoPeriodo(dataInicioNorm, dataFimNorm);
        const diasValidosSet = new Set(diasGerados.map(d => d.data));

        // Normalizar funcionários e filtrar produção apenas para dias válidos (chaves de data em YYYY-MM-DD)
        const funcionariosFormatados = funcionariosApi.map(func => {
          const prodDistinct = func.producao_diaria || {};
          const prodLeituras = func.producao_diaria_leituras || {};
          const filtradoDistinct = {};
          const filtradoLeituras = {};
          Object.entries(prodDistinct).forEach(([data, val]) => {
            const key = normalizarChaveData(data);
            if (diasValidosSet.has(key)) filtradoDistinct[key] = (filtradoDistinct[key] || 0) + (val || 0);
          });
          Object.entries(prodLeituras).forEach(([data, val]) => {
            const key = normalizarChaveData(data);
            if (diasValidosSet.has(key)) filtradoLeituras[key] = (filtradoLeituras[key] || 0) + (val || 0);
          });
          const totalDistinct = Object.values(filtradoDistinct).reduce((a,b)=>a+(b||0),0);
          const totalLeituras = Object.values(filtradoLeituras).reduce((a,b)=>a+(b||0),0);
          return {
            codigo: func.codigo,
            nome: func.nome,
            setor: func.setor,
            producaoDiariaDistinct: filtradoDistinct,
            producaoDiariaLeituras: filtradoLeituras,
            totalDistinct,
            totalLeituras
          };
        });

        // Totais por dia:
        // - DISTINCT: usa o distinct REAL do backend (COUNT(DISTINCT) por dia);
        //   somar as células infla porque a mesma requisição aparece em várias.
        // - LEITURAS (bruto): soma das células — leitura é aditiva, está correto.
        const distinctRealPorDiaApi = dadosTabular.totais_por_dia_distinct_real || {};
        const totaisDistinctFiltrados = diasGerados.reduce((acc, d) => {
          const real = distinctRealPorDiaApi[d.data];
          // Aceita número ou número-em-string (alguns drivers serializam COUNT assim);
          // só cai na soma das células se o distinct real estiver realmente ausente.
          acc[d.data] = (real != null && Number.isFinite(Number(real)))
            ? Number(real)
            : funcionariosFormatados.reduce((soma, f) => soma + (f.producaoDiariaDistinct[d.data] || 0), 0);
          return acc;
        }, {});
        const totaisLeiturasFiltrados = diasGerados.reduce((acc, d) => {
          acc[d.data] = funcionariosFormatados.reduce((soma, f) => soma + (f.producaoDiariaLeituras[d.data] || 0), 0);
          return acc;
        }, {});

        // Total distinct global REAL (não-aditivo) — vem do backend.
        // Aceita número ou número-em-string; fallback para soma só se ausente.
        const rawTotalDistinct = dadosTabular.total_distinct_real;
        const totalDistinctGlobal = (rawTotalDistinct != null && Number.isFinite(Number(rawTotalDistinct)))
          ? Number(rawTotalDistinct)
          : Object.values(totaisDistinctFiltrados).reduce((a, b) => a + (b || 0), 0);

        // Atualizar estados
        setDiasDoMes(diasGerados);
  setDadosProcessados(funcionariosFormatados);
  setTotaisDiarios(totaisDistinctFiltrados);
  setTotaisDiariosLeituras(totaisLeiturasFiltrados);
  setTotalDistinctReal(totalDistinctGlobal);
      } else {
        console.error('❌ Erro na resposta:', res);
        setError(res.message || 'Erro ao carregar dados');
      }
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      setError(error?.message || 'Erro ao carregar dados. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };
  // FUNÇÃO REMOVIDA - Os dados já vêm processados do backend

  // Definir período padrão (mês atual) - APENAS UMA VEZ
  useEffect(() => {
    const hoje = new Date();
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);

    setFiltros(prev => ({
      ...prev,
      dataInicio: primeiroDia.toISOString().split('T')[0],
      dataFim: ultimoDia.toISOString().split('T')[0]
    }));

    // Gerar dias do mês atual usando função centralizada
    const diasArray = gerarDiasDoPeriodo(
      primeiroDia.toISOString().split('T')[0],
      ultimoDia.toISOString().split('T')[0]
    );
    setDiasDoMes(diasArray);

    // Carregar opções de filtro apenas uma vez
    carregarOpcoesFiltro();
  }, [carregarOpcoesFiltro]); // inclui função nas dependências conforme ESLint

  // Remover o useEffect que reprocessa dados - não é mais necessário
  // pois os dados já vêm processados do backend

  // Aplicar cor baseada na produção
  const obterCorCelula = useCallback((valor) => {
    if (valor === 0) return '#ffebee';
    if (valor < 50) return '#fff3e0';
    if (valor < 100) return '#f3e5f5';
    if (valor < 150) return '#e8f5e8';
    return '#e3f2fd';
  }, []);

  // Aplicar cor do texto baseada na produção
  const obterCorTexto = useCallback((valor) => {
    if (valor === 0) return '#c62828';
    if (valor < 50) return '#ef6c00';
    if (valor < 100) return '#7b1fa2';
    if (valor < 150) return '#2e7d32';
    return '#1565c0';
  }, []);

  // Pré-calcular weekend set para evitar new Date() repetido
  const weekendSet = useMemo(() => {
    const set = new Set();
    diasDoMes.forEach(dia => {
      const dayOfWeek = new Date(dia.data + 'T12:00:00').getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) set.add(dia.data);
    });
    return set;
  }, [diasDoMes]);

  // Estilos fixos para evitar recriação a cada render
  const weekendDotStyle = useMemo(() => ({
    position: 'absolute',
    top: 2,
    right: 2,
    width: 6,
    height: 6,
    backgroundColor: '#757575',
    borderRadius: '50%'
  }), []);

  // Renderizar célula de produção (memoizado)
  const renderCelulaProducao = useCallback((valor, data) => {
    const isWeekend = weekendSet.has(data);
    
    return (
      <TableCell
        key={data}
        align="center"
        sx={{
          backgroundColor: obterCorCelula(valor),
          color: obterCorTexto(valor),
          fontWeight: valor > 0 ? 'bold' : 'normal',
          border: '1px solid #e0e0e0',
          minWidth: '60px',
          fontSize: '0.875rem',
          opacity: isWeekend ? 0.7 : 1,
          position: 'relative'
        }}
      >
        {valor > 0 ? valor : '-'}
        {isWeekend && <Box sx={weekendDotStyle} />}
      </TableCell>
    );
  }, [weekendSet, obterCorCelula, obterCorTexto, weekendDotStyle]);

  // Função para imprimir relatório completo
  const handlePrint = () => {
    setImprimindo(true);

    // Criar uma nova janela para impressão
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      alert('Bloqueador de pop-ups impediu a abertura da janela de impressão. Por favor, permita pop-ups para este site.');
      setImprimindo(false);
      return;
    }

    const metricaAtual = metrica === 'distinct' ? 'Requisições Distintas' : 'Total de Leituras';
    const totaisAtuais = metrica === 'distinct' ? totaisDiarios : totaisDiariosLeituras;
    const filialAtual = filtros.filialEstoque
      ? (opcoesFiltro.filiais.find(f => f.codigo === filtros.filialEstoque)?.nome || filtros.filialEstoque)
      : 'Todas';

    // Filtrar dados para impressão (remover SOMA TOTAL)
    const dadosParaImprimir = dadosProcessados.filter(
      func => func.codigo !== 'TOTAL' && func.setor !== 'SOMA TOTAL'
    );

    // Gerar HTML para impressão com TODOS os dados
    const htmlContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Relatório Tabular - ${filtros.dataInicio} a ${filtros.dataFim}</title>
          <style>
            @media print {
              @page {
                size: landscape;
                margin: 10mm;
              }
              body {
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 10px;
              margin: 0;
              padding: 10px;
            }
            
            h1 {
              text-align: center;
              color: #333;
              font-size: 18px;
              margin-bottom: 5px;
            }
            
            .info {
              text-align: center;
              color: #666;
              font-size: 11px;
              margin-bottom: 10px;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 9px;
            }
            
            th, td {
              border: 1px solid #ddd;
              padding: 4px;
              text-align: center;
            }
            
            th {
              background-color: #667eea;
              color: white;
              font-weight: bold;
              position: sticky;
              top: 0;
            }
            
            .funcionario-col {
              text-align: left;
              max-width: 180px;
              min-width: 150px;
              padding: 6px 4px;
              vertical-align: top;
            }
            
            .total-row {
              background-color: #f0f0f0;
              font-weight: bold;
            }
            
            .weekend {
              background-color: #f5f5f5;
            }
            
            .valor-alto {
              background-color: #4caf50;
              color: white;
              font-weight: bold;
            }
            
            .valor-medio {
              background-color: #8bc34a;
              font-weight: bold;
            }
            
            .valor-baixo {
              background-color: #ffc107;
            }
            
            .sem-producao {
              color: #999;
            }
          </style>
        </head>
        <body>
          <h1>Relatório Tabular de Produção</h1>
          <div class="info">
            <strong>Período:</strong> ${filtros.dataInicio} a ${filtros.dataFim} |
            <strong>Métrica:</strong> ${metricaAtual} |
            <strong>Setor:</strong> ${escapeHtml(filtros.setor || 'Todos')} |
            <strong>Filial Estoque:</strong> ${escapeHtml(filialAtual)} |
            <strong>Funcionários:</strong> ${dadosParaImprimir.length}
            <br>
            <strong>Gerado em:</strong> ${new Date().toLocaleString('pt-BR')}
          </div>
          
          <table>
            <thead>
              <tr>
                <th class="funcionario-col">Funcionário</th>
                ${diasDoMes.map(dia => `
                  <th class="${dia.diaSemana === 'sáb' || dia.diaSemana === 'dom' ? 'weekend' : ''}">
                    ${dia.dia}<br>${dia.diaSemana}
                  </th>
                `).join('')}
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${dadosParaImprimir.map(func => {
                const producao = metrica === 'distinct' ? func.producaoDiariaDistinct : func.producaoDiariaLeituras;
                const total = metrica === 'distinct' ? func.totalDistinct : func.totalLeituras;
                
                return `
                  <tr>
                    <td class="funcionario-col" title="${escapeHtml(func.nome)} - ${escapeHtml(func.setor)}">
                      <div style="font-weight: bold; font-size: 9px;">${escapeHtml(func.nome)}</div>
                      <div style="font-size: 8px; color: #666; margin-top: 1px;">${escapeHtml(func.setor)}</div>
                    </td>
                    ${diasDoMes.map(dia => {
                      const valor = producao[dia.data] || 0;
                      const isWeekend = dia.diaSemana === 'sáb' || dia.diaSemana === 'dom';
                      let classe = isWeekend ? 'weekend' : '';
                      
                      if (valor >= 50) classe += ' valor-alto';
                      else if (valor >= 20) classe += ' valor-medio';
                      else if (valor >= 5) classe += ' valor-baixo';
                      else if (valor === 0) classe += ' sem-producao';
                      
                      return `<td class="${classe}">${valor > 0 ? valor : '-'}</td>`;
                    }).join('')}
                    <td style="font-weight: bold; background-color: #e3f2fd;">${total}</td>
                  </tr>
                `;
              }).join('')}
              
              <tr class="total-row">
                <td>TOTAL</td>
                ${diasDoMes.map(dia => {
                  const total = totaisAtuais[dia.data] || 0;
                  return `<td>${total > 0 ? total : '-'}</td>`;
                }).join('')}
                <td>${metrica === 'distinct' ? totalDistinctReal : Object.values(totaisAtuais).reduce((a, b) => a + b, 0)}</td>
              </tr>
            </tbody>
          </table>
          
          <script>
            window.onload = function() {
              window.print();
              // Opcional: fechar janela após impressão
              // window.onafterprint = function() { window.close(); };
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Resetar estado após um breve delay
    setTimeout(() => {
      setImprimindo(false);
    }, 1000);
  };

  // Filtrar linhas de SOMA TOTAL antes de paginar (memoizado para evitar re-render na troca de filtros)
  const dadosFiltrados = useMemo(() => dadosProcessados.filter(
    funcionario => funcionario.codigo !== 'TOTAL' && funcionario.setor !== 'SOMA TOTAL'
  ), [dadosProcessados]);

  const dadosPaginados = useMemo(() => dadosFiltrados.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  ), [dadosFiltrados, page, rowsPerPage]);

  // Verificação de segurança - garantir que todos os dados têm a estrutura correta
  const dadosSegurosPaginados = useMemo(() => dadosPaginados.map(funcionario => ({
    ...funcionario,
    producaoDiariaDistinct: funcionario.producaoDiariaDistinct || {},
    producaoDiariaLeituras: funcionario.producaoDiariaLeituras || {},
    totalDistinct: funcionario.totalDistinct || 0,
    totalLeituras: funcionario.totalLeituras || 0
  })), [dadosPaginados]);

  // Memoizar totais para evitar recálculo em cada render.
  // Distinct usa o total REAL (não-aditivo) do backend; leituras soma os dias.
  const totalGeral = useMemo(() =>
    metrica === 'distinct'
      ? totalDistinctReal
      : Object.values(totaisDiariosLeituras).reduce((a, b) => a + b, 0),
    [metrica, totalDistinctReal, totaisDiariosLeituras]
  );

  // Conta pessoas únicas. Quando filtra por funcionário com múltiplos setores,
  // o backend devolve 1 linha por (funcionário, setor) — mas o card "Funcionários"
  // deve refletir pessoas distintas, não linhas da tabela.
  const totalFuncionariosUnicos = useMemo(() =>
    new Set(dadosFiltrados.map(f => f.codigo)).size,
    [dadosFiltrados]
  );

  const mediaPorPessoa = useMemo(() =>
    totalFuncionariosUnicos > 0 ? Math.round(totalGeral / totalFuncionariosUnicos) : 0,
    [totalGeral, totalFuncionariosUnicos]
  );

  const executarValidacao = async () => {
    if (!filtros.dataInicio || !filtros.dataFim) {
      setError('Selecione o período para validar');
      return;
    }
    try {
      setValidando(true);
      setResultadoValidacao(null);
      const parametros = { 
        dataInicio: `${filtros.dataInicio} ${filtros.horaInicio}:00`, 
        dataFim: `${filtros.dataFim} ${filtros.horaFim}:59` 
      };
      if (filtros.setor) parametros.setor = filtros.setor;
      if (funcionarioSelecionado) parametros.funcionario = funcionarioSelecionado.codigo;
      if (filtros.filialEstoque) parametros.filialEstoque = filtros.filialEstoque;
      const res = await api.relatorioTabularValidar(parametros);
      setResultadoValidacao(res);
    } catch (e) {
      console.error('Erro na validação', e);
    } finally {
      setValidando(false);
    }
  };

  return (
    <Box sx={{ 
      p: 3,
      '@media print': {
        '& .no-print': { display: 'none !important' },
        '& .MuiTableContainer-root': { maxHeight: 'none !important' },
        '& .MuiTable-root': { fontSize: '10px !important' },
        '& .MuiTableCell-root': { padding: '2px !important' }
      }
    }}>

      <Typography variant="h4" gutterBottom>
        Relatório Tabular de Produção
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Início"
              type="date"
              value={filtros.dataInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Data Fim"
              type="date"
              value={filtros.dataFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, dataFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Hora Início"
              type="time"
              value={filtros.horaInicio}
              onChange={(e) => setFiltros(prev => ({ ...prev, horaInicio: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Hora Fim"
              type="time"
              value={filtros.horaFim}
              onChange={(e) => setFiltros(prev => ({ ...prev, horaFim: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Setor</InputLabel>
              <Select
                value={filtros.setor}
                onChange={(e) => {
                  const val = e.target.value;
                  startTransition(() => {
                    setFiltros(prev => ({ ...prev, setor: val }));
                  });
                }}
                label="Setor"
              >
                <MenuItem value="">Todos</MenuItem>
                {opcoesFiltro.setores.map(setor => (
                  <MenuItem key={setor.id} value={setor.nome}>
                    {setor.nome}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Filial de Estoque</InputLabel>
              <Select
                value={filtros.filialEstoque}
                onChange={(e) => {
                  const val = e.target.value;
                  startTransition(() => {
                    setFiltros(prev => ({ ...prev, filialEstoque: val }));
                  });
                }}
                label="Filial de Estoque"
              >
                <MenuItem value="">Todas</MenuItem>
                {opcoesFiltro.filiais
                  .filter(filial => FILIAIS_ESTOQUE_PERMITIDAS.includes(String(filial.codigo)))
                  .map(filial => (
                    <MenuItem key={filial.codigo} value={filial.codigo}>
                      {filial.nome} ({filial.codigo})
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete
              options={opcoesFiltro.funcionarios}
              getOptionLabel={(option) => option.nome}
              value={funcionarioSelecionado}
              onChange={(event, newValue) => {
                setFuncionarioSelecionado(newValue);
                setFiltros(prev => ({ ...prev, funcionario: newValue ? newValue.codigo : '' }));
              }}
              renderInput={(params) => (
                <TextField {...params} label="Funcionário" fullWidth />
              )}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Métrica</InputLabel>
              <Select
                value={metrica}
                label="Métrica"
                onChange={(e) => {
                  const val = e.target.value;
                  startTransition(() => {
                    setMetrica(val);
                  });
                }}
              >
                <MenuItem value="distinct">Requisições Únicas (Distinct)</MenuItem>
                <MenuItem value="leituras">Total de Leituras (Bruto)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                onClick={carregarDados}
                disabled={loading}
              >
                Aplicar Filtros
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={dadosFiltrados.length === 0 || imprimindo}
              >
                {imprimindo ? 'Preparando...' : 'Imprimir'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                onClick={() => window.location.reload()}
              >
                Limpar Filtros
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={executarValidacao}
                disabled={validando}
              >
                {validando ? 'Validando...' : 'Validar Dados'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Estatísticas resumo */}
      {dadosFiltrados.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {totalFuncionariosUnicos}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Funcionários
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {totalGeral}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {metrica === 'distinct' ? 'Total Distinct' : 'Total Leituras'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {diasDoMes.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Dias no Período
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Box textAlign="center">
                <Typography variant="h6" color="primary">
                  {mediaPorPessoa}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Média por Pessoa ({metrica === 'distinct' ? 'Distinct' : 'Leituras'})
                </Typography>
              </Box>
            </Grid>
          </Grid>
          {metrica === 'distinct' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
              Os totais Distinct contam cada requisição uma única vez (não somam as células),
              por isso podem ser menores que a soma da linha/coluna quando a mesma requisição
              passa por mais de um setor, pessoa ou dia.
            </Typography>
          )}
        </Paper>
      )}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Carregando dados...</Typography>
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Tabela Principal */}
      {dadosFiltrados.length > 0 && (
        <Paper>
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f5f5f5', 
                      minWidth: '120px',
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Setor
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 'bold', 
                      backgroundColor: '#f5f5f5', 
                      minWidth: '200px',
                      position: 'sticky',
                      left: '120px',
                      zIndex: 3,
                      borderRight: '2px solid #e0e0e0'
                    }}
                  >
                    Nome do Colaborador
                  </TableCell>
                  
                  {diasDoMes.map(dia => (
                    <TableCell
                      key={dia.data}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#f5f5f5',
                        minWidth: '60px',
                        fontSize: '0.75rem',
                        padding: '8px 4px'
                      }}
                    >
                      <Box>
                        <Typography variant="caption" display="block">
                          Dia {dia.dia}
                        </Typography>
                        <Typography variant="caption" display="block" color="text.secondary">
                          {dia.diaSemana}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                  
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5', minWidth: '80px' }}
                  >
                    Total
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {dadosSegurosPaginados
                  .map((funcionario, index) => (
                  <TableRow
                    key={funcionario.codigo}
                    sx={{
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        border: '1px solid #e0e0e0',
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        backgroundColor: 'background.paper',
                        borderRight: '2px solid #e0e0e0',
                        minWidth: '120px'
                      }}
                    >
                      <Chip
                        label={funcionario.setor}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>

                    <TableCell
                      sx={{
                        fontWeight: 'bold',
                        border: '1px solid #e0e0e0',
                        position: 'sticky',
                        left: '120px',
                        zIndex: 2,
                        backgroundColor: 'background.paper',
                        borderRight: '2px solid #e0e0e0',
                        minWidth: '200px'
                      }}
                    >
                      {funcionario.nome}
                    </TableCell>

                    {diasDoMes.map(dia => {
                      const valor = metrica === 'distinct'
                        ? (funcionario.producaoDiariaDistinct[dia.data] || 0)
                        : (funcionario.producaoDiariaLeituras[dia.data] || 0);
                      return renderCelulaProducao(valor, dia.data);
                    })}

                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {metrica === 'distinct' ? funcionario.totalDistinct : funcionario.totalLeituras}
                    </TableCell>
                  </TableRow>
                ))}

                {/* Linha de totais */}
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 'bold', border: '1px solid #e0e0e0' }} colSpan={2}>
                    <Typography variant="h6">TOTAL</Typography>
                  </TableCell>

                  {diasDoMes.map(dia => (
                    <TableCell
                      key={dia.data}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        backgroundColor: '#e8f5e8',
                        color: '#2e7d32',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      {(metrica === 'distinct' ? totaisDiarios[dia.data] : totaisDiariosLeituras[dia.data]) || 0}
                    </TableCell>
                  ))}

                  <TableCell
                    align="center"
                    sx={{
                      fontWeight: 'bold',
                      backgroundColor: '#e3f2fd',
                      color: '#1565c0',
                      border: '1px solid #e0e0e0',
                      fontSize: '1.1rem'
                    }}
                  >
                    {totalGeral}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          <TablePagination
            className="no-print"
            component="div"
            count={dadosFiltrados.length}
            page={page}
            onPageChange={(event, newPage) => startTransition(() => setPage(newPage))}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              const val = parseInt(event.target.value, 10);
              startTransition(() => {
                setRowsPerPage(val);
                setPage(0);
              });
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
            }
          />
        </Paper>
      )}

      {/* Legenda de cores */}
      {dadosProcessados.length > 0 && (
        <Paper sx={{ p: 2, mt: 3 }} className="no-print">
          <Typography variant="h6" gutterBottom>
            Legenda de Cores
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#ffebee', border: '1px solid #ccc' }} />
                <Typography variant="body2">0 (Sem produção)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#fff3e0', border: '1px solid #ccc' }} />
                <Typography variant="body2">1-49 (Baixa)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#f3e5f5', border: '1px solid #ccc' }} />
                <Typography variant="body2">50-99 (Média)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#e8f5e8', border: '1px solid #ccc' }} />
                <Typography variant="body2">100-149 (Boa)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 20, height: 20, backgroundColor: '#e3f2fd', border: '1px solid #ccc' }} />
                <Typography variant="body2">150+ (Excelente)</Typography>
              </Box>
            </Grid>
            <Grid item xs={6} sm={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 8, height: 8, backgroundColor: '#757575', borderRadius: '50%' }} />
                <Typography variant="body2">Final de semana</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Resultado da Validação */}
      {resultadoValidacao && (
        <Paper sx={{ p:2, mt:3 }} className="no-print">
          <Typography variant="h6" gutterBottom>Resultado da Validação</Typography>
          {resultadoValidacao.success && resultadoValidacao.dados ? (
            <>
              <Typography variant="body2" gutterBottom>
                Período: {resultadoValidacao.dados.periodo.inicio} a {resultadoValidacao.dados.periodo.fim}
              </Typography>
              <Typography variant="body2" gutterBottom>
                Funcionários considerados: {resultadoValidacao.dados.funcionarios}
              </Typography>
              <Typography variant="body2" gutterBottom color={resultadoValidacao.dados.ok ? 'green' : 'error'}>
                Status: {resultadoValidacao.dados.ok ? 'OK - Sem divergências' : 'Divergências encontradas'}
              </Typography>
              {resultadoValidacao.dados.divergencias.length > 0 && (
                <Box sx={{ mt:2 }}>
                  <Typography variant="subtitle2">Divergências:</Typography>
                  <Table size="small" sx={{ mt:1 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Tipo</TableCell>
                        <TableCell>Funcionário</TableCell>
                        <TableCell>Esperado</TableCell>
                        <TableCell>Calculado</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {resultadoValidacao.dados.divergencias.map((d, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{d.tipo}</TableCell>
                          <TableCell>{d.codigo || '-'}</TableCell>
                          <TableCell>{d.esperado}</TableCell>
                          <TableCell>{d.calculado}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </>
          ) : (
            <Alert severity="error">Erro ao validar</Alert>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default RelatorioTabular;
